import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowPathIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { videoApi } from '@/services/videoApi';
import { textToSpeechApi, translationApi } from '@/services/audioApi';
import { audioToVideoApi, CreateAudioToVideoRequest, AudioToVideoJob } from '@/services/audioToVideoApi';
import CreateVideoWizard, { CreateVideoWizardState } from '@/components/CreateVideo/CreateVideoWizard';
import PublishResultView from '@/components/CreateVideo/PublishResultView';

const INITIAL_STATE: CreateVideoWizardState = {
  step: 0,
  maxReached: 0,
  title: '',
  titleOverlayEnabled: false,
  audioSourceType: 'ttsJob',
  selectedTtsJobId: null,
  selectedTranslationJobId: null,
  uploadedAudioFileId: null,
  uploadedAudioFileName: null,
  audioUploading: false,
  backgroundImageId: null,
  captionsEnabled: false,
  transcriptText: '',
  captionPosition: 'bottom',
  captionFontFamily: 'auto',
  aspectRatio: '16:9',
};

const CreateVideoPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<CreateVideoWizardState>(() => {
    // Quick-launch deep link from the Text to Speech / Audio Translation
    // screens (?ttsJobId=... or ?translationJobId=...) — pre-select the
    // source so the user lands on step 0 with it already chosen rather than
    // having to re-find it in the picker list.
    const ttsJobId = searchParams.get('ttsJobId');
    const translationJobId = searchParams.get('translationJobId');
    // Newsroom's "Create video from this" action deep-links with the
    // article's headline pre-filled as the title and the overlay
    // pre-checked, so publishing straight into video needs no extra typing.
    const title = searchParams.get('title');
    const titleOverlayEnabled = searchParams.get('embedTitle') === '1';
    const titleOverrides = title ? { title, titleOverlayEnabled } : {};

    if (translationJobId) {
      return { ...INITIAL_STATE, ...titleOverrides, audioSourceType: 'translationJob', selectedTranslationJobId: translationJobId };
    }
    if (ttsJobId) {
      return { ...INITIAL_STATE, ...titleOverrides, audioSourceType: 'ttsJob', selectedTtsJobId: ttsJobId };
    }
    return INITIAL_STATE;
  });
  // Deep link straight to an existing job's progress/result view (e.g. a
  // "View video" link from a published Newsroom article) rather than the
  // creation wizard — ?jobId=... skips step 0 entirely.
  const [activeJobId, setActiveJobId] = useState<string | null>(() => searchParams.get('jobId'));

  // Pre-fill the transcript from the selected source's own text — this is
  // the only timing-free caption source available for TTS/translation-
  // sourced audio. Tracks *which* selection the current transcript was
  // derived from (rather than just "is it empty") so re-selecting a
  // different audio source always refreshes the transcript to match —
  // guarding on emptiness alone left the previous job's text behind after
  // switching sources, since a non-empty transcript short-circuited the
  // effect regardless of whether it still matched the new selection.
  const prefilledTranscriptForId = useRef<string | null>(null);

  const { data: ttsJobsData } = useQuery({
    queryKey: ['tts-jobs-for-video'],
    queryFn: () => textToSpeechApi.getJobs({ status: 'completed', limit: 50 }),
    enabled: state.audioSourceType === 'ttsJob',
  });
  const { data: translationJobsData } = useQuery({
    queryKey: ['translation-jobs-for-video'],
    queryFn: () => translationApi.getTranslationJobs({ status: 'completed', limit: 50 }),
    enabled: state.audioSourceType === 'translationJob',
  });
  useEffect(() => {
    const selectedId =
      state.audioSourceType === 'ttsJob'
        ? state.selectedTtsJobId
        : state.audioSourceType === 'translationJob'
        ? state.selectedTranslationJobId
        : null;
    if (!selectedId || prefilledTranscriptForId.current === selectedId) return;

    let text: string | undefined;
    if (state.audioSourceType === 'ttsJob') {
      text = (ttsJobsData?.data?.jobs ?? []).find((j: any) => j.jobId === selectedId)?.request?.text;
    } else {
      text = (translationJobsData?.data?.jobs ?? []).find((j: any) => j.jobId === selectedId)?.result?.translatedText;
    }
    // Jobs list for this source type hasn't loaded yet — leave the ref
    // unset so this re-runs once ttsJobsData/translationJobsData arrives.
    if (text === undefined) return;

    prefilledTranscriptForId.current = selectedId;
    setState((s) => ({ ...s, transcriptText: text || '' }));
  }, [state.audioSourceType, state.selectedTtsJobId, state.selectedTranslationJobId, ttsJobsData, translationJobsData]);

  const uploadAudioMutation = useMutation({
    mutationFn: (file: File) => videoApi.uploadAudioFile(file),
    onMutate: () => setState((s) => ({ ...s, audioUploading: true })),
    onSuccess: (res: any) => {
      setState((s) => ({ ...s, audioUploading: false, uploadedAudioFileId: res.fileId }));
      toast.success('Audio uploaded');
    },
    onError: (err: any) => {
      setState((s) => ({ ...s, audioUploading: false }));
      toast.error(err.response?.data?.message || 'Failed to upload audio');
    },
  });

  const { data: jobData } = useQuery({
    queryKey: ['audio-to-video-job', activeJobId],
    queryFn: () => audioToVideoApi.getJob(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (data) => {
      const status = data?.job?.status;
      return status === 'processing' || status === 'queued' ? 2000 : false;
    },
  });
  const activeJob = jobData?.job;

  const generateMutation = useMutation({
    mutationFn: () => {
      const request: CreateAudioToVideoRequest = {
        title: state.title || undefined,
        titleOverlay: { enabled: state.titleOverlayEnabled && !!state.title.trim() },
        audioSource:
          state.audioSourceType === 'ttsJob'
            ? { sourceType: 'ttsJob', ttsJobId: state.selectedTtsJobId! }
            : state.audioSourceType === 'translationJob'
            ? { sourceType: 'translationJob', translationJobId: state.selectedTranslationJobId! }
            : { sourceType: 'upload', videoUploadFileId: state.uploadedAudioFileId! },
        transcriptText: state.captionsEnabled ? state.transcriptText : undefined,
        backgroundImageId: state.backgroundImageId!,
        captions: { enabled: state.captionsEnabled, position: state.captionPosition, fontFamily: state.captionFontFamily },
        aspectRatio: state.aspectRatio,
      };
      return audioToVideoApi.createJob(request);
    },
    onSuccess: (res) => {
      toast.success('Video generation started');
      setActiveJobId(res.jobId);
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to start video generation'),
  });

  const retryMutation = useMutation({
    mutationFn: () => audioToVideoApi.retryJob(activeJobId!),
    onSuccess: () => {
      toast.success('Retrying');
      queryClient.invalidateQueries({ queryKey: ['audio-to-video-job', activeJobId] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to retry'),
  });

  const handleStartOver = () => {
    prefilledTranscriptForId.current = null;
    setState(INITIAL_STATE);
    setActiveJobId(null);
  };

  // Plain "go back to the wizard" — unlike handleStartOver/startEditFrom,
  // this never touches `state` at all. `state` is never cleared when a job
  // starts (generateMutation's onSuccess only sets activeJobId), so it
  // already holds exactly what was submitted — same step, same
  // maxReached, every field — so going back just means stop watching this
  // job and show the wizard again, no reconstruction needed. Works from
  // any job status, including mid-flight ('queued'/'processing'), where
  // there was previously no way back at all. Cancels the job first if it's
  // still running, so the user isn't left with an orphaned job silently
  // rendering in the background while they go edit and resubmit.
  const handleBackToEditor = () => {
    if (activeJob && (activeJob.status === 'processing' || activeJob.status === 'queued')) {
      audioToVideoApi.cancelJob(activeJob.jobId).catch(() => {
        // Best-effort — if cancel fails (e.g. already finished by the time
        // this fires), still go back; the job just keeps running unseen.
      });
    }
    setActiveJobId(null);
  };

  // Lets the user go back into the wizard from a completed/failed job to
  // tweak the audio source or the caption transcript, pre-filled with
  // everything that job already had — rather than starting over from
  // scratch. Regenerating always creates a NEW job (the old job's rendered
  // output/VideoUpload doc is left untouched), since the pipeline's cached
  // per-step artifacts are tied to the original jobId's work directory and
  // can't be safely reused once the source inputs change. maxReached is set
  // to the last step so every step is immediately clickable — all the data
  // is already valid, there's no need to re-walk the wizard linearly.
  const startEditFrom = (job: AudioToVideoJob, initialStep: number) => {
    // Lock the pre-fill effect against the restored selection up front —
    // job.transcriptText may already be a hand-edited version that differs
    // from the source job's raw text, and should never be silently
    // overwritten by the auto-fill effect right after this restores it.
    prefilledTranscriptForId.current = job.audioSource.ttsJobId || job.audioSource.translationJobId || null;
    setState({
      step: initialStep,
      maxReached: 3,
      title: job.title || '',
      titleOverlayEnabled: job.titleOverlay?.enabled ?? false,
      audioSourceType: job.audioSource.sourceType,
      selectedTtsJobId: job.audioSource.ttsJobId || null,
      selectedTranslationJobId: job.audioSource.translationJobId || null,
      uploadedAudioFileId: job.audioSource.videoUploadFileId || null,
      uploadedAudioFileName: job.audioSource.sourceType === 'upload' ? (job.title || 'Previously uploaded audio') : null,
      audioUploading: false,
      backgroundImageId: job.backgroundImage.backgroundImageId,
      captionsEnabled: job.captions.enabled,
      transcriptText: job.transcriptText || '',
      captionPosition: job.captions.position || 'bottom',
      captionFontFamily: job.captions.fontFamily || 'auto',
      aspectRatio: job.aspectRatio,
    });
    setActiveJobId(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        {activeJob && (
          <button
            onClick={handleBackToEditor}
            title="Back to editor — this stops watching the current job without losing your settings"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
        )}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create Video</h1>
      </div>

      {!activeJob ? (
        <CreateVideoWizard
          state={state}
          setState={setState}
          onUploadAudio={(file) => uploadAudioMutation.mutate(file)}
          onGenerate={() => generateMutation.mutate()}
          generating={generateMutation.isLoading}
        />
      ) : activeJob.status === 'completed' ? (
        <PublishResultView
          job={activeJob}
          onStartOver={handleStartOver}
          onEditAudio={() => startEditFrom(activeJob, 0)}
          onEditTranscript={() => startEditFrom(activeJob, 2)}
        />
      ) : activeJob.status === 'failed' ? (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
          <p className="text-sm text-red-700 dark:text-red-400 mb-4">
            Video generation failed{activeJob.error ? `: ${activeJob.error}` : '.'}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50"
            >
              {retryMutation.isLoading ? 'Retrying…' : 'Retry'}
            </button>
            <button
              onClick={() => startEditFrom(activeJob, 0)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Edit audio
            </button>
            <button
              onClick={() => startEditFrom(activeJob, 2)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Edit transcript
            </button>
            <button onClick={handleStartOver} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              Start over
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
          <ArrowPathIcon className="h-8 w-8 mx-auto mb-4 text-blue-600 animate-spin" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">{activeJob.currentStep || 'Processing…'}</p>
          <div className="w-full max-w-sm mx-auto mt-3 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${activeJob.progress}%` }} />
          </div>
          <button
            onClick={handleBackToEditor}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Cancel &amp; go back to editor
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateVideoPage;
